import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-at";

/**
 * React.lazy that survives stale chunk hashes after a new deploy.
 * If the dynamic import fails (old hashed file no longer on the CDN),
 * force a one-time hard reload so the browser fetches the new index.html.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      // Only reload once per 10s window to avoid infinite loops.
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        // Never resolves — the page is going away.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
